import { create } from 'zustand';
import CONSTANTS from '../config/constants';

/**
 * Load audio device preferences with priority order
 * 
 * Loads device preferences using the following priority:
 * 1. Settings from props (passed to SessionViewer)
 * 2. Saved preferences from localStorage
 * 3. System defaults (will be determined at runtime by LiveKit)
 * 
 * Device preferences are stored as objects containing both deviceId and label
 * to enable label-based matching across sessions (more reliable than ID-only matching).
 * 
 * @param {Object} [propsSettings] - Settings passed via SessionViewer props
 * @param {Object} [propsSettings.audio] - Audio settings from props
 * @param {Object} [propsSettings.audio.input] - Input device from props
 * @param {Object} [propsSettings.audio.output] - Output device from props
 * @returns {Object} Device preferences
 * @returns {Object|null} audioInputDevice - Microphone device { deviceId, label }
 * @returns {Object|null} audioOutputDevice - Speaker device { deviceId, label }
 * 
 * @private
 */
const loadDevicePreferences = (propsSettings) => {
    try {
        // Priority 1: Props settings
        if (propsSettings?.audio) {
            return {
                audioInputDevice: propsSettings.audio.input || null,
                audioOutputDevice: propsSettings.audio.output || null,
            };
        }

        // Priority 2: localStorage
        const audioInputDevice = localStorage.getItem(CONSTANTS.LOCALSTORAGE_KEYS.AUDIO_INPUT_DEVICE);
        const audioOutputDevice = localStorage.getItem(CONSTANTS.LOCALSTORAGE_KEYS.AUDIO_OUTPUT_DEVICE);

        return {
            audioInputDevice: audioInputDevice ? JSON.parse(audioInputDevice) : null,
            audioOutputDevice: audioOutputDevice ? JSON.parse(audioOutputDevice) : null,
        };
    } catch (error) {
        console.error('[SettingsStore] Failed to load device preferences:', error);
        return {
            audioInputDevice: null,
            audioOutputDevice: null,
        };
    }
};

/**
 * Create the Settings Store
 * 
 * Manages application settings including the Settings modal state and audio device preferences.
 * Audio device preferences are loaded with the following priority:
 * 1. Settings from SessionViewer props
 * 2. Saved preferences from localStorage
 * 3. System defaults (determined at runtime by LiveKit)
 * 
 * Device preferences are persisted to localStorage and include both deviceId and label
 * to enable reliable device matching across sessions even when device IDs change.
 * 
 * @param {Object} initialState - Optional initial state from SessionViewer props
 * @param {Object} [initialState.audio] - Audio settings from props
 * @param {Object} [initialState.audio.input] - Input device { deviceId, label }
 * @param {Object} [initialState.audio.output] - Output device { deviceId, label }
 * @returns {Function} Zustand store hook
 * 
 * @example
 * const SettingsStore = getStore('settingsStore');
 * SettingsStore.openSettingsModal('audio');
 * SettingsStore.setAudioInputDevice('device123', 'Blue Yeti Microphone');
 */
export const createSettingsStore = (initialState = {}) => {
    const savedPreferences = loadDevicePreferences(initialState);
    let isInitialLoad = true;

    return create((set, get) => ({
        /**
         * Whether the Settings modal is currently open
         * @type {boolean}
         */
        isSettingsModalOpen: false,

        /**
         * Currently active tab in the Settings modal ('audio' or 'general')
         * @type {string}
         */
        activeTab: 'audio',

        /**
         * Currently selected audio input device
         * @type {Object|null}
         * @property {string} deviceId - LiveKit device ID
         * @property {string} label - Human-readable device name
         */
        audioInputDevice: savedPreferences.audioInputDevice,

        /**
         * Currently selected audio output device
         * @type {Object|null}
         * @property {string} deviceId - LiveKit device ID
         * @property {string} label - Human-readable device name
         */
        audioOutputDevice: savedPreferences.audioOutputDevice,

        /**
         * Open the Settings modal
         * 
         * @param {string} [tab='audio'] - Which tab to open ('audio' or 'general')
         */
        openSettingsModal: (tab = 'audio') => {
            set({
                isSettingsModalOpen: true,
                activeTab: tab
            });
        },

        /**
         * Close the Settings modal
         */
        closeSettingsModal: () => {
            set({ isSettingsModalOpen: false });
        },

        /**
         * Set the active tab in the Settings modal
         * 
         * @param {string} tab - Tab to activate ('audio' or 'general')
         */
        setActiveTab: (tab) => {
            set({ activeTab: tab });
        },

        /**
         * Set the audio input device preference
         * 
         * Saves both deviceId and label to localStorage for reliable device matching
         * across sessions. Label-based matching is more reliable than ID-only matching
         * because device IDs can change when devices are unplugged/replugged.
         * 
         * @param {string} deviceId - LiveKit device ID
         * @param {string} label - Human-readable device name
         */
        setAudioInputDevice: (deviceId, label, skipEvent = false) => {
            try {
                const device = { deviceId, label };
                localStorage.setItem(CONSTANTS.LOCALSTORAGE_KEYS.AUDIO_INPUT_DEVICE, JSON.stringify(device));
                set({ audioInputDevice: device });

                // Fire onSettingsChange event (skip during initial load)
                if (!isInitialLoad && !skipEvent) {
                    get().storeState('eventStore').trigger('onSettingsChange', { change: { audio: { input: device } } });
                }
            } catch (error) {
                console.error('[SettingsStore] Failed to save audio input device to localStorage:', error);
                set({ audioInputDevice: { deviceId, label } });
            }
        },

        /**
         * Set the audio output device preference
         * 
         * Saves both deviceId and label to localStorage for reliable device matching
         * across sessions. Label-based matching is more reliable than ID-only matching
         * because device IDs can change when devices are unplugged/replugged.
         * 
         * @param {string} deviceId - LiveKit device ID
         * @param {string} label - Human-readable device name
         */
        setAudioOutputDevice: (deviceId, label, skipEvent = false) => {
            try {
                const device = { deviceId, label };
                localStorage.setItem(CONSTANTS.LOCALSTORAGE_KEYS.AUDIO_OUTPUT_DEVICE, JSON.stringify(device));
                set({ audioOutputDevice: device });

                // Fire onSettingsChange event (skip during initial load)
                if (!isInitialLoad && !skipEvent) {
                    get().storeState('eventStore').trigger('onSettingsChange', { change: { audio: { output: device } } });
                }
            } catch (error) {
                console.error('[SettingsStore] Failed to save audio output device to localStorage:', error);
                set({ audioOutputDevice: { deviceId, label } });
            }
        },

        /**
         * Get all current settings
         * 
         * Returns settings in the same format as the SessionViewer settings prop.
         * 
         * @returns {Object} Current settings
         */
        getSettings: () => {
            const state = get();
            return {
                audio: {
                    input: state.audioInputDevice,
                    output: state.audioOutputDevice,
                },
            };
        },

        /**
         * Update settings with partial updates
         * 
         * Merges the provided settings with existing settings. Only updates
         * the properties that are provided, leaving others unchanged.
         * 
         * @param {Object} settings - Partial settings to update
         * @param {Object} [settings.audio] - Audio settings
         * @param {Object} [settings.audio.input] - Input device { deviceId, label }
         * @param {Object} [settings.audio.output] - Output device { deviceId, label }
         */
        updateSettings: (settings) => {
            const changedSettings = {};

            if (settings?.audio) {
                if (settings.audio.input) {
                    const { deviceId, label } = settings.audio.input;
                    get().setAudioInputDevice(deviceId, label);
                    if (!changedSettings.audio) changedSettings.audio = {};
                    changedSettings.audio.input = settings.audio.input;
                }

                if (settings.audio.output) {
                    const { deviceId, label } = settings.audio.output;
                    get().setAudioOutputDevice(deviceId, label);
                    if (!changedSettings.audio) changedSettings.audio = {};
                    changedSettings.audio.output = settings.audio.output;
                }
            }
        },

        /**
         * Inject dependent stores
         * 
         * @param {Object} stores - Dependent stores
         * @private
         */
        injectDependentStores: (stores) => {
            set({ stores });
        },

        /**
         * Get state from a dependent store
         * 
         * @param {string} name - Store name
         * @returns {Object} Store state
         * @private
         */
        storeState: (name) => {
            if (!get().stores[name])
                throw new Error(`${name} is not a valid dependency in this store.`);
            return get().stores[name].getState();
        },

        /**
         * Mark initial load as complete
         * 
         * Called after initial device setup to enable event firing
         * @private
         */
        markInitialLoadComplete: () => {
            isInitialLoad = false;
        },

        ...initialState,
    }));
};
